import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  Bell, Check, CheckCheck, Trash2, Filter, Settings,
  UserPlus, Heart, MessageCircle, Trophy, Flame, Star, 
  Award, Gift, Crown, Calendar, ChefHat, X, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ICON_MAP = {
  'user-plus': UserPlus,
  'heart': Heart,
  'message-circle': MessageCircle,
  'trophy': Trophy,
  'flame': Flame,
  'star': Star,
  'award': Award,
  'gift': Gift,
  'crown': Crown,
  'calendar': Calendar,
  'check-circle': Check,
  'x-circle': X,
  'flag': Flame,
  'check': Check,
  'x': X,
  'bell': Bell,
};

const NOTIFICATION_FILTERS = [
  { value: 'all', label: 'Toutes' },
  { value: 'follow', label: 'Abonnements' },
  { value: 'like', label: 'Likes' },
  { value: 'barka_earned', label: 'BARKA' },
  { value: 'challenge_win', label: 'Challenges' },
  { value: 'booking_request', label: 'Réservations' },
];

export default function NotificationsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [filter, unreadOnly]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      if (unreadOnly) params.append('unread_only', 'true');
      params.append('limit', '100');
      
      const response = await api.get(`/notifications?${params.toString()}`);
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const clearReadNotifications = async () => {
    try {
      await api.delete('/notifications?read_only=true');
      setNotifications(notifications.filter(n => !n.is_read));
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Bell;
  };

  const groupNotificationsByDate = (notifications) => {
    const groups = {};
    notifications.forEach(n => {
      const date = format(new Date(n.created_at), 'yyyy-MM-dd');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(n);
    });
    return groups;
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const groupedNotifications = groupNotificationsByDate(notifications);

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="notifications-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white hover:bg-white/10 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <Bell className="w-7 h-7" />
              </div>
              <div>
                <h1 className="font-fraunces text-3xl font-bold">Notifications</h1>
                <p className="text-white/70">
                  {unreadCount > 0 ? `${unreadCount} non lues` : 'Tout est lu'}
                </p>
              </div>
            </div>
            
            {unreadCount > 0 && (
              <Button 
                variant="secondary" 
                className="rounded-full"
                onClick={markAllAsRead}
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Tout marquer lu
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px] rounded-full bg-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer par type" />
            </SelectTrigger>
            <SelectContent>
              {NOTIFICATION_FILTERS.map(f => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant={unreadOnly ? 'default' : 'outline'}
            className={`rounded-full ${unreadOnly ? 'bg-[#1E4ED8]' : 'bg-white'}`}
            onClick={() => setUnreadOnly(!unreadOnly)}
          >
            Non lues seulement
          </Button>

          {notifications.some(n => n.is_read) && (
            <Button
              variant="ghost"
              className="rounded-full text-[#D9381E] ml-auto"
              onClick={clearReadNotifications}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer les lues
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Card key={i} className="p-4 rounded-2xl">
                <div className="flex items-start gap-4">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-1/3" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <Card className="p-12 text-center rounded-2xl bg-white">
            <div className="w-20 h-20 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-10 h-10 text-[#1E4ED8]" />
            </div>
            <h3 className="font-fraunces text-xl font-bold mb-2">Aucune notification</h3>
            <p className="text-gray-500">
              {filter !== 'all' || unreadOnly 
                ? 'Aucune notification ne correspond à tes filtres'
                : 'Tu n\'as pas encore de notifications'}
            </p>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedNotifications).map(([date, dayNotifications]) => (
              <div key={date}>
                <h3 className="text-sm font-medium text-gray-500 mb-3">
                  {format(new Date(date), 'EEEE d MMMM', { locale: fr })}
                </h3>
                <div className="space-y-2">
                  {dayNotifications.map((notification) => {
                    const Icon = getIcon(notification.type_info?.icon);
                    const iconColor = notification.type_info?.color || '#6B7280';
                    
                    return (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`group relative bg-white rounded-2xl p-4 cursor-pointer transition-all hover:shadow-md ${
                          !notification.is_read ? 'ring-2 ring-[#1E4ED8]/20' : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-4">
                          {/* Icon */}
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${iconColor}15` }}
                          >
                            <Icon className="w-6 h-6" style={{ color: iconColor }} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                                {notification.title}
                              </p>
                              {!notification.is_read && (
                                <Badge className="bg-[#1E4ED8] text-white text-xs shrink-0">
                                  Nouveau
                                </Badge>
                              )}
                            </div>
                            <p className="text-gray-500 text-sm mt-1">
                              {notification.message}
                            </p>
                            <p className="text-gray-400 text-xs mt-2">
                              {formatDistanceToNow(new Date(notification.created_at), { 
                                addSuffix: true,
                                locale: fr 
                              })}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!notification.is_read && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
